import { createRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { platformLayoutRoute } from './platformLayout.js';
import { PlatformModal } from '../../components/platform/PlatformModal.js';
import {
  listScheduledJobs,
  listJobRuns,
  triggerJob,
  type ScheduledJob,
  type JobRunStatus,
} from '../../api/platformScheduledJobs.js';

/** Format an ISO timestamp as "YYYY-MM-DD HH:MM" (UTC), or em-dash when null. */
function fmt(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16);
}

function fmtDuration(ms: number | null | undefined): string {
  return ms == null ? '—' : `${(ms / 1000).toFixed(1)}s`;
}

const STATUS_CLASSES: Record<JobRunStatus, string> = {
  running: 'bg-indigo-primary/10 text-indigo-primary',
  success: 'bg-emerald/10 text-emerald',
  error: 'bg-red-600/10 text-red-600',
  partial: 'bg-amber/10 text-amber',
};

function JobStatusBadge({ status }: { status: JobRunStatus }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASSES[status]}`}
      aria-label={`Run status: ${status}`}
    >
      {status}
    </span>
  );
}

function ScheduledJobsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['platform', 'scheduled-jobs'],
    queryFn: listScheduledJobs,
  });
  const [historyJob, setHistoryJob] = useState<string | null>(null);

  const trigger = useMutation({
    mutationFn: (name: string) => triggerJob(name),
    onSuccess: () => {
      // The run is created synchronously; give the background tick a beat then
      // refetch so the operator sees it (running → terminal on the next refresh).
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['platform', 'scheduled-jobs'] });
      }, 500);
    },
  });

  return (
    <div className="p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-indigo-deep">Scheduled Jobs</h1>
          <p className="text-sm text-gray-500">In-process scheduler — job health, history, and manual triggers.</p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded border border-indigo-deep px-3 py-1.5 text-sm text-indigo-deep hover:bg-indigo-deep hover:text-white"
        >
          Refresh
        </button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" role="alert">
          Could not load jobs: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-3 py-2">Job</th>
                <th className="px-3 py-2">Schedule</th>
                <th className="px-3 py-2">Last Run</th>
                <th className="px-3 py-2">Result</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">Next Run</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.jobs ?? []).map((job: ScheduledJob) => (
                <tr key={job.name} className="border-t border-gray-100 align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-indigo-deep">{job.name}</div>
                    <div className="text-xs text-gray-500">{job.description}</div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <code className="text-xs">{job.cronPattern}</code> <span className="text-gray-400">{job.timezone}</span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(job.lastRun?.startedAt)}</td>
                  <td className="px-3 py-2">{job.lastRun ? <JobStatusBadge status={job.lastRun.status} /> : '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDuration(job.lastRun?.durationMs)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmt(job.nextRun)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      {job.manuallyTriggerable ? (
                        <button
                          type="button"
                          disabled={trigger.isPending}
                          onClick={() => {
                            if (window.confirm(`Trigger “${job.name}” now?`)) {
                              trigger.mutate(job.name);
                            }
                          }}
                          className="rounded bg-indigo-primary px-3 py-1 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
                        >
                          Trigger Now
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setHistoryJob(job.name)}
                        className="rounded border border-indigo-deep px-3 py-1 text-xs text-indigo-deep hover:bg-indigo-deep hover:text-white"
                      >
                        View History
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historyJob ? <HistoryModal jobName={historyJob} onClose={() => setHistoryJob(null)} /> : null}
    </div>
  );
}

function HistoryModal({ jobName, onClose }: { jobName: string; onClose: () => void }): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'scheduled-jobs', jobName, 'runs'],
    queryFn: () => listJobRuns(jobName, 20),
  });

  return (
    <PlatformModal
      open
      onClose={onClose}
      title={`${jobName} — run history`}
      footer={
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
        >
          Close
        </button>
      }
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (data?.runs.length ?? 0) === 0 ? (
        <p className="text-sm text-gray-500">No runs recorded yet.</p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-2 py-1.5">Started</th>
                <th className="px-2 py-1.5">Duration</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Triggered By</th>
                <th className="px-2 py-1.5">Error</th>
              </tr>
            </thead>
            <tbody>
              {(data?.runs ?? []).map((run) => (
                <tr key={run.id} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 whitespace-nowrap">{fmt(run.startedAt)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {run.status === 'running' ? 'running…' : fmtDuration(run.durationMs)}
                  </td>
                  <td className="px-2 py-1.5">
                    <JobStatusBadge status={run.status} />
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {run.triggeredBy === 'manual' ? `Manual (${run.triggeredByUser?.email ?? 'unknown'})` : 'Scheduler'}
                  </td>
                  <td className="px-2 py-1.5 text-red-600">{run.errorMessage ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PlatformModal>
  );
}

export const platformScheduledJobsRoute = createRoute({
  getParentRoute: () => platformLayoutRoute,
  path: '/platform/scheduled-jobs',
  component: ScheduledJobsPage,
});
